import { Validators } from 'core/services/Validators';
import { JobApplicantsStore } from 'employer/feature-job-applicants/job-applicatns-store';
import { action, computed, makeAutoObservable, observable } from 'mobx';
import { FormControl, FormGroup } from 'shared/libs/form';
import { AsyncState } from 'shared/libs/state';
import { ApiResponseDto } from 'shared/types';
import { SendEmailApiService } from './SendEmailToCandidateServie';

type JobSeeker = {
   id: number;
   email: string;
};

type EmailVariantSending = 'all' | 'single';

type EmailSingleDto = {
   slug: string;
   candidateId: number;
};

type EmailVariantMap = {
   single: EmailSingleDto;
   all: {};
};

export class SendEmailStore<T extends JobApplicantsStore = JobApplicantsStore> {
   private readonly emailState = new AsyncState<ApiResponseDto<string>>();
   private readonly sendEmailJobSeekerState = new AsyncState<ApiResponseDto<string>>();

   readonly form = new FormGroup({
      subject: new FormControl<string>('', [Validators.required]),
      message: new FormControl<string>('', [Validators.required]),
   });

   showDialog: boolean = false;

   constructor(private readonly jobApplicants: T) {
      makeAutoObservable(this, {
         showDialog: observable,
         toggleDialog: action,
         emailSending: computed,
      });
   }

   get emailSending() {
      return (
         this.emailState.status === 'pending' || this.sendEmailJobSeekerState.status === 'pending'
      );
   }

   toggleDialog(o: boolean): void {
      this.showDialog = o;
   }

   sendEmail<TVariant extends EmailVariantSending>(
      variant: TVariant,
      dto: EmailVariantMap[TVariant],
      params: {
         onSuccess?: (res: any) => void;
         onError?: (e: any) => void;
      }
   ) {
      if (variant === 'single' && 'slug' in dto) {
         this.sendEmailJobseeker(dto, params);
      }
      // if (variant === 'all') {
      //    this.sendEmailToAll();
      // }
   }

   async sendEmailToAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (res: any) => void;
      onError?: (e: any) => void;
   } = {}): Promise<void> {
      this.form.validate();
      if (!this.form.valid) {
         return;
      }

      const emails = this.jobApplicants.selectedCandidates.map((c) => c.email_address).join(',');
      const jobId = this.jobApplicants.jobInfo?.job_id;

      if (emails && jobId) {
         await this.emailState.run(
            () =>
               SendEmailApiService.sendEmailToAll({
                  job_id: jobId,
                  email_address: emails,
                  message: this.form.controls['message'].value,
                  subject: this.form.controls['subject'].value,
               }),
            {
               onSuccess: (res) => {
                  if (res) {
                     this.toggleDialog(false);
                     onSuccess?.(res);
                  }
               },
               onError: (e) => {
                  onError?.(e);
               },
            }
         );
      }
   }

   async sendEmailJobseeker(
      dto: EmailSingleDto,
      {
         onSuccess,
         onError,
      }: {
         onSuccess?: (res: any) => void;
         onError?: (e: any) => void;
      } = {}
   ): Promise<void> {
      this.form.validate();
      if (!this.form.valid) {
         return;
      }
      await this.sendEmailJobSeekerState.run(
         () =>
            SendEmailApiService.sendEmailJobseeker(
               {
                  id: dto.candidateId,
                  message: this.form.controls['message'].value,
                  subject: this.form.controls['subject'].value,
                  emailFiles: [],
                  selectedFileName: [],
               },
               {
                  slug: dto.slug,
               }
            ),
         {
            onSuccess: (res) => {
               if (res) {
                  this.toggleDialog(false);
                  onSuccess?.(res);
               }
            },
            onError: (e) => {
               onError?.(e);
            },
         }
      );
   }
}
